import {
  type FormEvent,
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { LoaderCircle, X } from "lucide-react";

import {
  CHANGE_PASSWORD_MAX_LENGTH,
  changePasswordInputSchema,
  type ChangePasswordInput,
} from "@learn-flow/contracts";

import { ApiError } from "../../lib/api.js";
import { changePassword } from "./settings-api.js";

type PasswordField = keyof ChangePasswordInput;
type PasswordErrors = Partial<Record<PasswordField, string>>;

const unavailableMessage =
  "Your password was not changed. MemoKite is temporarily unavailable; please try again.";

export function ChangePasswordDialog({
  onChanged,
  onDismiss,
  onSessionInvalid,
}: {
  onChanged: () => void;
  onDismiss: () => void;
  onSessionInvalid: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmNewPasswordRef = useRef<HTMLInputElement>(null);
  const requestInFlight = useRef(false);
  const headingId = useId();
  const descriptionId = useId();
  const [values, setValues] = useState<ChangePasswordInput>({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState<PasswordErrors>({});
  const [requestError, setRequestError] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const background = [
      ...document.querySelectorAll<HTMLElement>(
        "#main-content, .sidebar, .mobile-app-bar, .sidebar-visibility-control, .settings-topbar, .settings-layout",
      ),
    ];
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    for (const element of background) element.inert = true;
    if (!dialog.open) dialog.showModal();
    currentPasswordRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
      for (const element of background) element.inert = false;
    };
  }, []);

  const dismiss = (): void => {
    if (pending) return;
    setValues({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    setErrors({});
    setRequestError("");
    onDismiss();
  };

  const updateValue = (field: PasswordField, value: string): void => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setRequestError("");
  };

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (requestInFlight.current) return;
    const parsed = changePasswordInputSchema.safeParse(values);
    if (!parsed.success) {
      const nextErrors: PasswordErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (
          (field === "currentPassword" ||
            field === "newPassword" ||
            field === "confirmNewPassword") &&
          !nextErrors[field]
        ) {
          nextErrors[field] = issue.message;
        }
      }
      setErrors(nextErrors);
      setRequestError("");
      focusFirstInvalid(nextErrors, {
        currentPassword: currentPasswordRef.current,
        newPassword: newPasswordRef.current,
        confirmNewPassword: confirmNewPasswordRef.current,
      });
      return;
    }

    requestInFlight.current = true;
    setPending(true);
    setErrors({});
    setRequestError("");
    try {
      await changePassword(parsed.data);
      setValues({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
      onChanged();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setValues({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
        onSessionInvalid();
        return;
      }
      if (error instanceof ApiError && error.problem?.code === "INCORRECT_CURRENT_PASSWORD") {
        setValues((current) => ({ ...current, currentPassword: "" }));
        setErrors({ currentPassword: "Your current password is incorrect." });
        window.requestAnimationFrame(() => currentPasswordRef.current?.focus());
        return;
      }
      if (error instanceof ApiError && error.status === 400 && error.problem?.fieldErrors) {
        const nextErrors = problemFieldErrors(error.problem.fieldErrors);
        setErrors(nextErrors);
        focusFirstInvalid(nextErrors, {
          currentPassword: currentPasswordRef.current,
          newPassword: newPasswordRef.current,
          confirmNewPassword: confirmNewPasswordRef.current,
        });
        return;
      }
      setRequestError(unavailableMessage);
    } finally {
      requestInFlight.current = false;
      setPending(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="change-password-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        dismiss();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget || pending) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        ) {
          dismiss();
        }
      }}
      onKeyDown={(event) => trapDialogFocus(event, dialogRef.current)}
    >
      <form aria-busy={pending} onSubmit={(event) => void submit(event)} noValidate>
        <button
          type="button"
          className="change-password-close"
          aria-label="Close change password"
          disabled={pending}
          onClick={dismiss}
        >
          <X size={20} aria-hidden="true" />
        </button>
        <span className="section-kicker">Password security</span>
        <h2 id={headingId}>Change your password</h2>
        <p id={descriptionId}>
          Choose a new password for MemoKite. Other signed-in sessions will be securely logged out.
        </p>

        <PasswordInput
          ref={currentPasswordRef}
          id="current-password"
          label="Current password"
          value={values.currentPassword}
          autoComplete="current-password"
          help="Enter the password you use now."
          error={errors.currentPassword}
          disabled={pending}
          onChange={(value) => updateValue("currentPassword", value)}
        />
        <PasswordInput
          ref={newPasswordRef}
          id="new-password"
          label="New password"
          value={values.newPassword}
          autoComplete="new-password"
          help="Use 8–256 characters with at least one letter and one number."
          error={errors.newPassword}
          disabled={pending}
          onChange={(value) => updateValue("newPassword", value)}
        />
        <PasswordInput
          ref={confirmNewPasswordRef}
          id="confirm-new-password"
          label="Confirm new password"
          value={values.confirmNewPassword}
          autoComplete="new-password"
          help="Enter the same new password again."
          error={errors.confirmNewPassword}
          disabled={pending}
          onChange={(value) => updateValue("confirmNewPassword", value)}
        />

        <div className="change-password-message" aria-live="polite">
          {requestError ? (
            <p className="settings-inline-error" role="alert">
              {requestError}
            </p>
          ) : null}
        </div>
        <div className="change-password-actions">
          <button type="button" className="soft-button" disabled={pending} onClick={dismiss}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={pending}>
            {pending ? (
              <>
                <LoaderCircle className="change-password-spinner" size={16} aria-hidden="true" />
                Changing password…
              </>
            ) : (
              "Change password"
            )}
          </button>
        </div>
      </form>
    </dialog>
  );
}

const PasswordInput = forwardRef<
  HTMLInputElement,
  {
    id: string;
    label: string;
    value: string;
    autoComplete: "current-password" | "new-password";
    help: string;
    error: string | undefined;
    disabled: boolean;
    onChange: (value: string) => void;
  }
>(function PasswordInput({ id, label, value, autoComplete, help, error, disabled, onChange }, ref) {
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  return (
    <div className="change-password-field">
      <label htmlFor={id}>{label}</label>
      <input
        ref={ref}
        id={id}
        type="password"
        value={value}
        autoComplete={autoComplete}
        maxLength={CHANGE_PASSWORD_MAX_LENGTH}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={`${helpId} ${errorId}`}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      <p id={helpId} className="field-help">
        {help}
      </p>
      <p id={errorId} className="field-error" role={error ? "alert" : undefined}>
        {error ?? ""}
      </p>
    </div>
  );
});

function problemFieldErrors(fieldErrors: Record<string, string[]>): PasswordErrors {
  return {
    ...(fieldErrors.currentPassword?.[0]
      ? { currentPassword: fieldErrors.currentPassword[0] }
      : {}),
    ...(fieldErrors.newPassword?.[0] ? { newPassword: fieldErrors.newPassword[0] } : {}),
    ...(fieldErrors.confirmNewPassword?.[0]
      ? { confirmNewPassword: fieldErrors.confirmNewPassword[0] }
      : {}),
  };
}

function focusFirstInvalid(
  errors: PasswordErrors,
  elements: Record<PasswordField, HTMLInputElement | null>,
): void {
  for (const field of [
    "currentPassword",
    "newPassword",
    "confirmNewPassword",
  ] satisfies PasswordField[]) {
    if (errors[field]) {
      elements[field]?.focus();
      return;
    }
  }
}

function trapDialogFocus(
  event: ReactKeyboardEvent<HTMLDialogElement>,
  dialog: HTMLDialogElement | null,
): void {
  if (event.key !== "Tab" || !dialog) return;
  const controls = [
    ...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  ];
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
